import React, { useState, useEffect, useRef } from 'react';
import { aboutData } from '../data/mock';
import { Target, Users, Globe, Award, Sparkles } from 'lucide-react';

// Parallax floating element
const FloatingShape = ({ className, delay = 0, isDarkMode }) => (
  <div 
    className={`absolute rounded-full opacity-20 ${className}`}
    style={{
      animation: `float-gentle ${4 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
    }}
  />
);

// Animated counter component
const AnimatedCounter = ({ value, isVisible }) => {
  const [displayValue, setDisplayValue] = useState('0');
  
  // Parse the value to handle different formats like "50+", "100%", "$1.3M+"
  const parseValue = (val) => {
    // Check if it's a decimal number like $1.3M+
    const decimalMatch = val.match(/(\$?)(\d+\.?\d*)(.*)/);
    if (decimalMatch) {
      return {
        prefix: decimalMatch[1] || '',
        number: parseFloat(decimalMatch[2]),
        suffix: decimalMatch[3] || '',
        hasDecimal: decimalMatch[2].includes('.')
      };
    }
    return { prefix: '', number: 0, suffix: val, hasDecimal: false };
  };
  
  const parsed = parseValue(value);
  
  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 2000;
    const steps = 60;
    const increment = parsed.number / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= parsed.number) {
        setDisplayValue(parsed.hasDecimal ? parsed.number.toFixed(1) : Math.floor(parsed.number).toString());
        clearInterval(timer);
      } else {
        setDisplayValue(parsed.hasDecimal ? current.toFixed(1) : Math.floor(current).toString());
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [isVisible, parsed.number, parsed.hasDecimal]);
  
  return <span>{parsed.prefix}{displayValue}{parsed.suffix}</span>;
};

const About = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: Target, title: 'Academic Excellence', desc: 'Supporting high-achieving students to reach their full potential' },
    { icon: Users, title: 'Mentorship', desc: 'Connecting students with experienced mentors and counselors' },
    { icon: Globe, title: 'Global Citizenship', desc: 'Preparing students to become leaders in a globalized world' },
  ];

  const iconMap = [
    <Target key="target" className="w-6 h-6" />,
    <Users key="users" className="w-6 h-6" />,
    <Globe key="globe" className="w-6 h-6" />,
    <Award key="award" className="w-6 h-6" />,
  ];

  return (
    <section id="about" ref={sectionRef} className={`py-28 relative overflow-hidden ${isDarkMode ? 'bg-[#1a2625]' : 'bg-[#E5D9B6]'}`}>
      {/* Floating decorative shapes */}
      <FloatingShape className={`w-32 h-32 top-20 right-20 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} delay={0} isDarkMode={isDarkMode} />
      <FloatingShape className={`w-20 h-20 top-40 left-10 ${isDarkMode ? 'bg-[#284139]' : 'bg-[#284139]'}`} delay={0.5} isDarkMode={isDarkMode} />
      <FloatingShape className={`w-16 h-16 bottom-40 right-40 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} delay={1} isDarkMode={isDarkMode} />
      <FloatingShape className={`w-24 h-24 bottom-20 left-20 ${isDarkMode ? 'bg-[#809076]' : 'bg-[#284139]'}`} delay={1.5} isDarkMode={isDarkMode} />
      
      {/* Background decorations */}
      <div className={`absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#E5D9B6]/5' : 'bg-[#284139]/10'}`} />
      <div className={`absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#284139]/30' : 'bg-[#284139]/10'}`} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`max-w-3xl mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className={`inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full transition-colors cursor-default ${
            isDarkMode 
              ? 'text-[#E5D9B6] bg-[#E5D9B6]/10 border border-[#E5D9B6]/20 hover:bg-[#E5D9B6]/20'
              : 'text-[#284139] bg-[#284139]/10 border border-[#284139]/20 hover:bg-[#284139]/20'
          }`}>
            <Sparkles size={14} className="animate-pulse" />
            About Us
          </span>
          <h2 className={`font-display text-4xl lg:text-6xl font-bold mb-6 leading-tight ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#284139]'}`}>
            {aboutData.title}
          </h2>
          <div className={`w-24 h-1.5 rounded-full ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} />
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left Column - Text */}
          <div className="space-y-8">
            <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-[#809076]' : 'text-[#284139]/80'}`}>
              {aboutData.description}
            </p>
            <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-[#809076]' : 'text-[#284139]/80'}`}>
              {aboutData.mission}
            </p>

            {/* Mission Points - Premium card style */}
            <div className="mt-10 space-y-5">
              {features.map((feature, index) => (
                <div 
                  key={feature.title}
                  className={`group flex items-start gap-5 p-5 rounded-2xl border transition-all duration-300 hover-lift ${
                    isDarkMode 
                      ? 'bg-[#111A19]/60 border-[#284139] hover:border-[#E5D9B6]/40'
                      : 'bg-[#284139] border-[#284139] hover:border-[#E5D9B6]/40'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-[#E5D9B6]/20 to-[#284139] group-hover:from-[#E5D9B6]/30'
                      : 'bg-[#E5D9B6] group-hover:bg-[#d4c9a5]'
                  }`}>
                    <feature.icon className={`w-5 h-5 ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1.5 text-lg ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>{feature.title}</h4>
                    <p className={`text-sm ${isDarkMode ? 'text-[#809076]/80' : 'text-[#E5D9B6]/70'}`}>{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Stats Cards */}
          <div className="grid grid-cols-2 gap-5">
            {aboutData.stats.map((stat, index) => (
              <div
                key={stat.label}
                className="group p-7 rounded-2xl border transition-all duration-300 hover-lift relative overflow-hidden bg-[#E5D9B6] border-[#284139] hover:border-[#284139]"
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#284139]/0 to-[#284139]/0 group-hover:from-[#284139]/5 group-hover:to-transparent transition-all duration-500" />
                
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#284139] flex items-center justify-center mb-5 text-[#E5D9B6] group-hover:bg-[#1a2625] transition-colors duration-300">
                    {iconMap[index]}
                  </div>
                  <p className="text-4xl font-bold font-display text-[#284139] mb-2">
                    <AnimatedCounter value={stat.value} isVisible={isVisible} />
                  </p>
                  <p className="text-sm text-[#284139]/80">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
